import {makeObservable, observable, runInAction} from "mobx";
import {VideoWatchTimeHandler} from "./video-watch-time-handler";
import {Check, TimeSpan} from "@sparkware/app-utils";
import {isVideoFatalError, IVideoAnalyticsReporter} from "./analytics/video-analytics-reporter.interface";
import {IVideoResolution} from "./video-player.view-model.interface";


export class VideoEventsController {
    constructor(private readonly videoElement: HTMLVideoElement,
                private readonly getAnalyticsReporter: () => IVideoAnalyticsReporter) {

        this.videoElement.addEventListener('playing', this._onPlaying);
        this.videoElement.addEventListener('canplay', this._onCanPlay);
        this.videoElement.addEventListener('stalled', this._onStalled);
        this.videoElement.addEventListener('waiting', this._onWaiting);
        this.videoElement.addEventListener('error', this._onError);
        this.videoElement.addEventListener('emptied', this._onEmptied);
        this.videoElement.addEventListener('pause', this._onPause);
        this.videoElement.addEventListener('timeupdate', this._onTimeUpdate);
        this.videoElement.addEventListener('ended', this._onEnded);
        this.videoElement.addEventListener('resize', this._onResize);
        window.addEventListener('offline', this._onGoingOffline);


        this._watchTimeHandler = new VideoWatchTimeHandler();

        makeObservable(this);
    }

    destroy(): void {
        this.videoElement.removeEventListener('playing', this._onPlaying);
        this.videoElement.removeEventListener('canplay', this._onCanPlay);
        this.videoElement.removeEventListener('stalled', this._onStalled);
        this.videoElement.removeEventListener('waiting', this._onWaiting);
        this.videoElement.removeEventListener('error', this._onError);
        this.videoElement.removeEventListener('emptied', this._onEmptied);
        this.videoElement.removeEventListener('pause', this._onPause);
        this.videoElement.removeEventListener('timeupdate', this._onTimeUpdate);
        this.videoElement.removeEventListener('ended', this._onEnded);
        this.videoElement.removeEventListener('resize', this._onResize);
        window.removeEventListener('offline', this._onGoingOffline);

    }

    private get analyticsReporter(): IVideoAnalyticsReporter {
        return this.getAnalyticsReporter();
    }

    private _bufferingStartTime: number | null = null;

    @observable.ref
    private _watchTimeHandler: VideoWatchTimeHandler;

    get watchTime(): TimeSpan {
        return this._watchTimeHandler.watchTime;
    }

    resetWatchTime(): void {
        runInAction(() => {
            this._watchTimeHandler = new VideoWatchTimeHandler();
        })
    }

    @observable.ref
    private _isLoading: boolean = true;
    get isLoading(): boolean {
        return this._isLoading;
    }


    @observable.ref
    private _isBuffering: boolean = false;
    get isBuffering(): boolean {
        return this._isBuffering;
    }

    @observable.ref
    private _hasError: boolean = false;
    get hasError(): boolean {
        return this._hasError || this._isOffline;
    }

    @observable.ref
    private _isOffline: boolean = false;


    @observable.ref
    private _isPlaying: boolean = false;
    get isPlaying(): boolean {
        return this._isPlaying;
    }

    @observable.ref
    private _isPaused: boolean = false;
    get isPaused(): boolean {
        return this._isPaused;
    }

    private _onEmptied = () => {
        runInAction(() => {
            this._previousResolution = null;
            this._isLoading = true;
            this._hasError = false;
            this._onBufferingStopped();
            this._isPlaying = false;
            this._isPaused = false;
            this.resetWatchTime();
            this._lastReportedDroppedFrames = 0;
        });
    }

    private _onPlaying = () => {
        runInAction(() => {
            this._hasError = false;
            this._isLoading = false;
            this._onBufferingStopped();
            this._isPlaying = true;
            this._isPaused = false;
            this._isOffline = false;
            this._watchTimeHandler.onPlayStarted();
        })
    }

    private _onBufferingStarted(): void {
        runInAction(() => {
            this._isBuffering = true;
            if(Check.isNullOrUndefined(this._bufferingStartTime)) {
                this._bufferingStartTime = Date.now();
            }
        })
    }

    private _onBufferingStopped(): void {
        if(this._isBuffering && !Check.isNullOrUndefined(this._bufferingStartTime)) {
            this.analyticsReporter.reportBufferingTime(TimeSpan.fromMilliseconds(Date.now() -  this._bufferingStartTime));
        }

        runInAction(() => {
            this._isBuffering = false;
            this._bufferingStartTime = null;
        })
    }

    private _onStalled = () => {
        runInAction(() => {
            this._hasError = false;
            this._isLoading = false;
            this._onBufferingStarted();
            this._isPlaying = false;
            this._isPaused = false;
            this._watchTimeHandler.onPlayStopped();
        })
    }

    private _onWaiting = () => {
        runInAction(() => {
            this._hasError = false;
            //this._isLoading - on purpose not set.
            this._onBufferingStarted();
            this._isPlaying = false;
            this._isPaused = false;
            this._watchTimeHandler.onPlayStopped();
        })
    }

    private _onCanPlay = () => {
        runInAction(() => {
            this._hasError = false;
            this._isPaused = false;
        })
    }




    private _onError = (event: Event) => {

        const error = (event as any).target?.error;
        if(Check.isNullOrUndefined(error)) {
            // some time we get the error event when we pause the video while the video is loading.
            // In this case the error is null, and we will ignore this case.
            return;
        }

        let hasError: boolean = isVideoFatalError(error);

        runInAction(() => {

            this._hasError = hasError;
            this._isLoading = false;
            this._onBufferingStopped();
            this._isPlaying = false;
            this._isPaused = false;
            this._watchTimeHandler.onPlayStopped();
        });

        if(hasError) {
            this.analyticsReporter.reportPlayError(error);
        }
    }

    private _onPause = () => {
        runInAction(() => {
            this._hasError = false;
            this._isLoading = false;
            this._isBuffering = false;
            this._isPlaying = false;
            this._isPaused = true;
            this._watchTimeHandler.onPlayStopped();
        });
    }

    private _lastReportedDroppedFrames: number = 0;
    private _onTimeUpdate = () => {
        if(this._isPlaying) {
            this._watchTimeHandler.updateWatchTime();
        }

        const playbackQuality = this.videoElement.getVideoPlaybackQuality();

        if(playbackQuality.droppedVideoFrames > 0 && this._lastReportedDroppedFrames !== playbackQuality.droppedVideoFrames) {
            this._lastReportedDroppedFrames = playbackQuality.droppedVideoFrames;
            this.analyticsReporter.reportDroppedFrames(playbackQuality.droppedVideoFrames);
        }
    }

    private _onEnded = () => {
        this._watchTimeHandler.updateWatchTime();
    }

    private _previousResolution: IVideoResolution | null = null;
    private _onResize = () => {
        // on iOS, when the video is loaded for the first time we get a "resize" event with width=0 and height=0.
        // since there is no value in this event trigger, we choose to ignore it
        if (this.videoElement.videoWidth === 0 || this.videoElement.videoHeight === 0) {
            return;
        }

        const newResolution: IVideoResolution = {
            width: this.videoElement.videoWidth,
            height: this.videoElement.videoHeight
        };

        if(this._previousResolution && (this._previousResolution.width !== newResolution.width || this._previousResolution.height !== newResolution.height)) {
            this.analyticsReporter.reportVideoResolutionChanged(this._previousResolution, newResolution);
        }

        this._previousResolution = newResolution;
    }

    private _onGoingOffline = () => {
        runInAction(() => {
            this._isOffline = true;
        })
    }


}